import type { Vec3 } from '../geo/types';
import type { CityTimeSnapshot } from './cityTime';

export interface Daylight {
  readonly exposure: number;
  readonly tint: Vec3;
  readonly sun: Vec3;
  readonly activity: number;
  readonly elevation: number;
  /** 0 with the sun up, 1 once civil twilight has ended: how far flames show and light the street. */
  readonly night: number;
  /** The share of the evening's household lights still burning (burningShare). */
  readonly burning: number;
}

/*
 * Household lights through the night, as the share of the houses that light one at all. The hours
 * are the user's (7 October 2026): the most candles from seven to ten in the evening, fewer and
 * fewer until two, when the last have gone to sleep, and more again from about five as people wake.
 * A candle by a sickbed here and there burns through. The shares themselves are estimates.
 */
const BURNING: readonly (readonly [minutes: number, share: number])[] = [
  [0, 0.42],
  [1 * 60, 0.18],
  [2 * 60, 0.03],
  [4.5 * 60, 0.03],
  [5 * 60, 0.22],
  [6 * 60, 0.42],
  [16 * 60, 0.42],
  [17 * 60, 0.6],
  [19 * 60, 1],
  [22 * 60, 1],
  [23 * 60, 0.7],
  [24 * 60, 0.42],
];

export function burningShare(minutes: number): number {
  const at = ((minutes % 1440) + 1440) % 1440;
  let [from, share] = [0, 0];
  for (const [to, next] of BURNING) {
    if (at <= to && to > from) return share + ((next - share) * (at - from)) / (to - from);
    [from, share] = [to, next];
  }
  return share;
}

const RAD = Math.PI / 180;
/*
 * What the night keeps of the day's light. The night is never wholly dark: there is always some
 * moonshine (the user, 6 October 2026), so the city stays legible and the flames still carry. As a
 * share of daylight this is far more than a real moon gives; a screen cannot show the true ratio,
 * and 0.02 drew as black. Art direction, as the day's exposure is. The moon itself, its phase and
 * its place in the sky are not modelled.
 */
export const NIGHT_EXPOSURE = 0.08;
/* Moonlight's colour on the painting's ochres: cooler than the day's, so night reads as night and
   not as a dim afternoon. */
const MOON_TINT = [0.3, 0.5, 0.86] as const;
function smooth(from: number, to: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - from) / (to - from)));
  return t * t * (3 - 2 * t);
}

const LATITUDE = 52.37 * RAD;

/* The sun's declination, in radians, and the equation of time, in minutes, on a day of the year. */
function solar(dayOfYear: number): { declination: number; equation: number } {
  const yearAngle = ((2 * Math.PI) / 365) * (dayOfYear - 1);
  return {
    declination:
      0.006918 -
      0.399912 * Math.cos(yearAngle) +
      0.070257 * Math.sin(yearAngle) -
      0.006758 * Math.cos(2 * yearAngle) +
      0.000907 * Math.sin(2 * yearAngle) -
      0.002697 * Math.cos(3 * yearAngle) +
      0.00148 * Math.sin(3 * yearAngle),
    equation:
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(yearAngle) -
        0.032077 * Math.sin(yearAngle) -
        0.014615 * Math.cos(2 * yearAngle) -
        0.040849 * Math.sin(2 * yearAngle)),
  };
}

/* The civil minutes at which the sun stands at an hour angle, the inverse of daylight's own. */
const LONGITUDE_MINUTES = 4 * 4.9;

/** Sunrise and sunset, in minutes of the civil day: when the sun's middle crosses the horizon. */
export interface DayWindow {
  readonly sunrise: number;
  readonly sunset: number;
}

/** The day's sunrise and sunset at Amsterdam, by the same approximation as `daylight`. */
export function dayWindow(time: Pick<CityTimeSnapshot, 'dayOfYear' | 'utcOffset'>): DayWindow {
  const { declination, equation } = solar(time.dayOfYear);
  const cosHour = -Math.tan(LATITUDE) * Math.tan(declination);
  const hour = Math.acos(Math.min(1, Math.max(-1, cosHour))) / RAD;
  const noon = 180 * 4 + time.utcOffset - LONGITUDE_MINUTES - equation;
  return { sunrise: noon - hour * 4, sunset: noon + hour * 4 };
}

/** Seasonal solar approximation at Amsterdam. Weather, moonlight and exposure are art direction. */
export function daylight(time: CityTimeSnapshot): Daylight {
  const { declination, equation } = solar(time.dayOfYear);
  const hourAngle =
    ((time.minutes - time.utcOffset + LONGITUDE_MINUTES + equation) / 4 - 180) * RAD;
  const latitude = LATITUDE,
    sinD = Math.sin(declination),
    cosD = Math.cos(declination);
  const up = Math.sin(latitude) * sinD + Math.cos(latitude) * cosD * Math.cos(hourAngle);
  const elevation = Math.asin(up) / RAD;
  const day = smooth(-7, 10, elevation),
    warm = day * (1 - smooth(0, 20, elevation));
  return {
    elevation,
    exposure: NIGHT_EXPOSURE + (1 - NIGHT_EXPOSURE) * day,
    tint: [
      MOON_TINT[0] + (1 - MOON_TINT[0]) * day,
      MOON_TINT[1] + (1 - MOON_TINT[1]) * day - 0.12 * warm,
      MOON_TINT[2] + (1 - MOON_TINT[2]) * day - 0.25 * warm,
    ],
    sun: [
      -cosD * Math.sin(hourAngle),
      Math.max(0.08, up),
      -(Math.cos(latitude) * sinD - Math.sin(latitude) * cosD * Math.cos(hourAngle)),
    ],
    activity: smooth(-3, 8, elevation),
    night: 1 - smooth(-6, 0, elevation),
    burning: burningShare(time.minutes),
  };
}
